import React, { useCallback } from "react";
import type html2canvas from "html2canvas";

interface PrintDepthChartProps {
  depthChartRef: React.RefObject<HTMLDivElement>;
  selectedSide: string;
  selectedDepthChart: { name: string } | null;
}

export const usePrintDepthChart = (depthChartRef: React.RefObject<HTMLDivElement>, selectedSide: string, selectedDepthChart: { name: string } | null) => {
  return useCallback(async () => {
    if (!depthChartRef.current) return;

    try {
      const { default: html2canvas } = await import("html2canvas");

      const canvas = await html2canvas(depthChartRef.current, {
        backgroundColor: "white",
        scale: 2,
        logging: false,
        useCORS: true,
      });

      const printWindow = window.open("", "_blank");

      if (!printWindow) {
        alert("Please allow popups for this website to print the depth chart.");
        return;
      }

      const imageDataURL = canvas.toDataURL("image/png");

      printWindow.document.write(`
        <html>
          <head>
            <title>${selectedDepthChart?.name} - ${selectedSide}</title>
            <style>
              @page {
                size: landscape;
              }
              body {
                margin: 0;
                padding: 20px;
                display: flex;
                justify-content: center;
                background-color: white;
                color: black;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              @media print {
                body {
                  padding: 0;
                  background-color: white !important;
                  color: black !important;
                }
              }
            </style>
          </head>
          <body>
            <img src="${imageDataURL}" alt="${selectedDepthChart?.name} - ${selectedSide}" />
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() {
                    window.close();
                  };
                }, 300);
              };
            </script>
          </body>
        </html>
      `);

      printWindow.document.close();
    } catch (error) {
      console.error("Error generating print version:", error);
      alert("There was an error preparing the chart for printing.");
    }
  }, [depthChartRef, selectedSide, selectedDepthChart]);
};

const PrintDepthChart: React.FC<PrintDepthChartProps> = ({
  depthChartRef,
  selectedSide,
  selectedDepthChart,
}) => {
  const handlePrint = usePrintDepthChart(depthChartRef, selectedSide, selectedDepthChart);

  return (
    <button onClick={handlePrint}>Print</button>
  );
};

export default PrintDepthChart; 